import { addDays, endOfMonth, isAfter, isBefore, parseISO, startOfDay, startOfMonth } from 'date-fns'
import { diaCalendario } from '@/lib/diaCalendario'

import type { EstadisticasVencimientos, VencimientoConCliente } from '@/types/vencimientos'
import type { AsignacionConcepto, TipoConcepto } from '@/core/services/conceptosService'
import type { MapaResponsables } from '@/core/services/responsablesService'
import type { ItemCalendario, ItemsDelDia } from '@/core/types/calendario'
import { tareaAsignadaAMi, type TareaPropiedad } from '@/lib/permisos-guard'

/**
 * Estado efectivo de un vencimiento. La vista SQL (`vista_proximos_vencimientos`)
 * calcula `estado_efectivo`, que deriva 'vencido' por fecha (`< CURRENT_DATE`).
 * El `estado` crudo NUNCA vale 'vencido' (un vencido real queda como 'pendiente'
 * hasta que se presenta/paga), así que clasificar por `estado` deja los vencidos
 * sin contar. Usar siempre este helper para clasificar por estado.
 */
export const estadoEfectivoVencimiento = (v: VencimientoConCliente): string =>
  v.estado_efectivo ?? v.estado ?? ''

export interface ConteoVencimientosPorEstado {
  totalPendientes: number
  totalPresentados: number
  totalVencidos: number
  totalPagados: number
}

/**
 * Cuenta vencimientos por estado efectivo, para los badges del grid mensual del
 * calendario. Clasifica por `estado_efectivo` para que los vencidos reales
 * (pendientes con fecha pasada) se cuenten como 'vencido' y no como 'pendiente'.
 */
export const contarVencimientosPorEstado = (
  vencimientos: VencimientoConCliente[],
): ConteoVencimientosPorEstado => {
  const conteo: ConteoVencimientosPorEstado = {
    totalPendientes: 0,
    totalPresentados: 0,
    totalVencidos: 0,
    totalPagados: 0,
  }

  for (const v of vencimientos) {
    switch (estadoEfectivoVencimiento(v)) {
      case 'pendiente':
        conteo.totalPendientes++
        break
      case 'presentado':
        conteo.totalPresentados++
        break
      case 'vencido':
        conteo.totalVencidos++
        break
      case 'pagado':
        conteo.totalPagados++
        break
    }
  }

  return conteo
}

/**
 * Índice del reparto de conceptos: `cliente:tipo:concepto` → quiénes lo tienen a cargo.
 *
 * La clave incluye el cliente porque el mismo impuesto puede estar repartido en un cliente y
 * libre en otro; sin el cliente, asignar IVA en uno lo escondería en todos.
 */
export type IndiceReparto = Map<string, Set<string>>

const claveReparto = (clienteId: string, tipo: TipoConcepto, conceptoId: string): string =>
  `${clienteId}:${tipo}:${conceptoId}`

export const indexarReparto = (asignaciones: AsignacionConcepto[]): IndiceReparto => {
  const indice: IndiceReparto = new Map()

  for (const a of asignaciones) {
    const clave = claveReparto(a.clienteId, a.tipo, a.conceptoId)
    const duenos = indice.get(clave)
    if (duenos) duenos.add(a.usuarioId)
    else indice.set(clave, new Set([a.usuarioId]))
  }

  return indice
}

/**
 * El concepto de un vencimiento, o null si no se puede identificar. Un vencimiento cargado a
 * mano sin impuesto ni obligación es un caso real, no un dato roto.
 */
/**
 * Lo mínimo que hace falta para resolver a quién le corresponde un vencimiento.
 *
 * Se tipa el subconjunto en vez de exigir `VencimientoConCliente` entero porque las tarjetas de
 * estadísticas traen solo estas columnas (ORR-382): con el tipo completo habría que castear, y un
 * cast se vuelve mentira en silencio el día que la audiencia mire un campo más.
 */
export type ConAudiencia = Pick<
  VencimientoConCliente,
  'cliente_id' | 'impuesto_id' | 'obligacion_recurrente_id'
>

const conceptoDeVencimiento = (
  v: ConAudiencia,
): { tipo: TipoConcepto; id: string } | null => {
  if (v.impuesto_id) return { tipo: 'impuesto', id: v.impuesto_id }
  if (v.obligacion_recurrente_id) return { tipo: 'obligacion', id: v.obligacion_recurrente_id }
  return null
}

/**
 * A quién le corresponde un vencimiento. **El reparto manda sobre la responsabilidad del
 * cliente**: un concepto repartido es de sus dueños, sea o no responsable del cliente; uno sin
 * repartir queda a cargo de todos los responsables, que es el default y el estado de casi todos.
 *
 * Es la definición única de audiencia: el filtro por responsable y la lista de opciones del
 * multi-select salen las dos de acá. Tenerla duplicada fue el bug de ORR-370.
 *
 * El orden importa (ORR-369). Preguntar primero por el cliente escondía el caso que ORR-303 vino a
 * habilitar —delegar un impuesto SIN delegar el cliente entero—: la policy
 * `vencimientos_fiscales_select_scoping_responsable` es
 * `es_responsable_o_admin(cliente_id) OR tiene_concepto_asignado(...)`, así que la base ya le
 * mostraba ese vencimiento al asignado y solo lo tapaba este filtro. `mapaResponsables` sale de
 * `cliente_responsables` y nunca va a saber de las asignaciones por concepto: la única forma de que
 * la UI diga lo mismo que la RLS es consultar el reparto primero.
 *
 * Que no abre de más: al asignado le corresponde ESE concepto, no el resto del cliente — un
 * vencimiento hermano sin repartir vuelve a la rama de responsables y le queda afuera.
 */
const audienciaDeVencimiento = (
  v: ConAudiencia,
  mapaResponsables: MapaResponsables,
  reparto: IndiceReparto,
): ReadonlySet<string> | undefined => {
  if (!v.cliente_id) return undefined

  const concepto = conceptoDeVencimiento(v)
  const duenos = concepto
    ? reparto.get(claveReparto(v.cliente_id, concepto.tipo, concepto.id))
    : undefined

  return duenos ?? mapaResponsables.get(v.cliente_id)
}

export const vencimientoCorrespondeA = (
  v: ConAudiencia,
  usuarioId: string,
  mapaResponsables: MapaResponsables,
  reparto: IndiceReparto,
): boolean => audienciaDeVencimiento(v, mapaResponsables, reparto)?.has(usuarioId) ?? false

/**
 * A quiénes tiene sentido ofrecer en el multi-select "Responsable" de la planilla: la unión de las
 * audiencias de los vencimientos a la vista.
 *
 * Sale de `audienciaDeVencimiento` y no de `mapaResponsables` a secas porque esa era la falla
 * (ORR-370): a quien solo tiene conceptos repartidos no se lo podía elegir, aunque el filtro sí lo
 * resolvía bien desde ORR-369 — se le podía repartir un concepto y después no filtrar por él.
 * Compartir la definición también acota por el otro lado: un responsable cuyo único vencimiento
 * está repartido a otro no se ofrece, porque elegirlo dejaría la planilla vacía.
 */
export const usuariosConVencimientos = (
  vencimientos: VencimientoConCliente[],
  mapaResponsables: MapaResponsables,
  reparto: IndiceReparto,
): Set<string> => {
  const usuarios = new Set<string>()

  for (const v of vencimientos) {
    const audiencia = audienciaDeVencimiento(v, mapaResponsables, reparto)
    if (audiencia) for (const id of audiencia) usuarios.add(id)
  }

  return usuarios
}

/**
 * Quiénes tienen algún concepto repartido en cada cliente: `clienteId` → usuarios.
 *
 * Alimenta el filtro cruzado de la planilla, que acota la lista de clientes por el responsable
 * elegido. Esa lista salía solo de `cliente_responsables`, así que al elegir a un asignado por
 * concepto —recién ofrecido por `usuariosConVencimientos`— el selector de clientes quedaba en
 * "Sin resultados".
 *
 * Acá la unión con los responsables del cliente es lo correcto y no contradice a
 * `audienciaDeVencimiento`: aquella decide por concepto, esta responde si el cliente tiene algo de
 * esa persona. Un mismo cliente puede tener un concepto repartido a uno y el resto a cargo de sus
 * responsables, y las dos combinaciones son elegibles.
 */
export const indexarAsignadosPorCliente = (
  asignaciones: AsignacionConcepto[],
): Map<string, Set<string>> => {
  const indice = new Map<string, Set<string>>()

  for (const a of asignaciones) {
    const usuarios = indice.get(a.clienteId)
    if (usuarios) usuarios.add(a.usuarioId)
    else indice.set(a.clienteId, new Set([a.usuarioId]))
  }

  return indice
}

export interface ScopingResponsable {
  /** Owner/Admin ve todo. Al filtrar por un responsable elegido a mano va en `false`. */
  esAdmin: boolean
  usuarioId: string | null
  mapaResponsables: MapaResponsables
  reparto: IndiceReparto
}

/** Las dos posiciones del interruptor de la planilla (ORR-367). */
export type VistaPlanilla = 'mios' | 'estudio'

/**
 * El scoping efectivo de la planilla, según el interruptor "Míos / Todo el estudio" (ORR-367).
 *
 * `esAdmin` deja de salir solo del rol y pasa a salir también del interruptor: un admin en "Míos"
 * se comporta como un colaborador y ve únicamente lo suyo. Es todo el cambio que pidió Lisandro —
 * la lógica de pertenencia, reparto de conceptos incluido, no se toca.
 *
 * El `&&` es lo que impide que el interruptor sea un bypass: quien NO es admin queda scopeado
 * elija lo que elija, así que exponer el control por error no abre nada. El enforcement real sigue
 * siendo la RLS del server; esto es scoping de UI.
 */
export const resolverScoping = (
  base: ScopingResponsable,
  vista: VistaPlanilla,
): ScopingResponsable => ({
  ...base,
  esAdmin: base.esAdmin && vista === 'estudio',
})

/**
 * Scoping por responsable: deja solo los vencimientos que le corresponden al usuario.
 *
 * Reemplaza al viejo match contra `cliente_responsable`, el `string_agg` de nombres que emite la
 * vista: ahí un apellido con coma se parte en dos responsables inexistentes, dos homónimos son la
 * misma persona y un usuario sin nombre cargado no matchea con nada. Nada de eso le pasa al id,
 * que además es la clave con la que se reparten los conceptos (ORR-230 fase 4).
 *
 * Sin `usuarioId` resuelto no filtra: es scoping de UI y el enforcement real es server-side, así
 * que ante la duda muestra de más y no deja la planilla vacía (ORR-205).
 */
export const filtrarVencimientosPorResponsable = <T extends ConAudiencia>(
  vencimientos: T[],
  { esAdmin, usuarioId, mapaResponsables, reparto }: ScopingResponsable,
): T[] => {
  if (esAdmin || !usuarioId) return vencimientos
  return vencimientos.filter((v) =>
    vencimientoCorrespondeA(v, usuarioId, mapaResponsables, reparto),
  )
}

/**
 * La pertenencia de una tarea del calendario, que no se decide por responsable del cliente sino
 * por quién la tiene asignada (`tareaAsignadaAMi`). Vale igual para la tarea real y la proyectada:
 * las dos traen `asignado_a` en `datos_originales`.
 */
export interface ScopingTareas {
  /** Email del usuario en sesión. En null no filtra: es scoping de UI, ante la duda muestra de más. */
  emailUsuario: string | null
}

const itemDeTareaEsPropio = (item: ItemCalendario, emailUsuario: string): boolean =>
  tareaAsignadaAMi(item.datos_originales as unknown as TareaPropiedad, emailUsuario)

/**
 * Mismo scoping, aplicado a los ítems unificados del calendario (vencimientos + eventos + tareas).
 *
 * `calendarioService.obtenerItemsPorMes` filtra por organización y nada más, así que esta fuente
 * llegaba sin scopear a la grilla mensual y al dialog del día — a diferencia de `vencimientosPorDia`,
 * que sí lo tenía desde ORR-205. Mientras el grid solo pintaba puntitos la diferencia no se veía;
 * con los ítems dentro de la celda (ORR-296) un asistente leería el impuesto y el cliente ajenos.
 *
 * Cada tipo de ítem se scopea por SU criterio de pertenencia: el vencimiento por responsable del
 * cliente, la tarea y la proyectada por `tareaAsignadaAMi`. **Los eventos siguen pasando enteros**:
 * su pertenencia es `created_by` + `participantes`, que no está definida todavía, y esconderlos
 * por la mitad sería peor que mostrarlos.
 *
 * `scopingTareas` es obligatorio a propósito: con un argumento opcional, la próxima superficie que
 * llame a esta función se olvida de pasarlo y nace con el mismo bug, en silencio.
 */
export const filtrarItemsPorResponsable = (
  itemsPorDia: ItemsDelDia[],
  scoping: ScopingResponsable,
  scopingTareas: ScopingTareas,
): ItemsDelDia[] => {
  if (scoping.esAdmin || !scoping.usuarioId) return itemsPorDia
  const { usuarioId, mapaResponsables, reparto } = scoping

  const resultado: ItemsDelDia[] = []

  for (const dia of itemsPorDia) {
    const items = dia.items.filter((item) => {
      if (item.tipo === 'vencimiento') {
        return vencimientoCorrespondeA(
          item.datos_originales as unknown as VencimientoConCliente,
          usuarioId,
          mapaResponsables,
          reparto,
        )
      }
      if (item.tipo === 'tarea' || item.tipo === 'tarea_proyectada') {
        const { emailUsuario } = scopingTareas
        return !emailUsuario || itemDeTareaEsPropio(item, emailUsuario)
      }
      return true
    })
    // Un día que se queda sin ítems no va: la celda quedaría clickeable y vacía.
    if (items.length === 0) continue

    const totales = { totalVencimientos: 0, totalEventos: 0, totalTareas: 0, totalPendientes: 0 }
    for (const item of items) {
      if (item.tipo === 'vencimiento') totales.totalVencimientos++
      else if (item.tipo === 'evento') totales.totalEventos++
      else if (item.tipo === 'tarea') totales.totalTareas++
      if (item.estado === 'pendiente') totales.totalPendientes++
    }

    resultado.push({ ...dia, items, ...totales })
  }

  return resultado
}

/**
 * Las columnas de `vista_proximos_vencimientos` que necesitan las cinco tarjetas de estadísticas.
 *
 * Las tres de `ConAudiencia` no estaban en el `select` original, y esa era toda la causa de ORR-382:
 * sin ellas no se podía saber a quién le corresponde cada fila, así que los recuadros contaban todo
 * el estudio aunque la planilla de abajo estuviera filtrada.
 */
export interface FilaEstadistica extends ConAudiencia {
  estado: string
  fecha_vencimiento: string
  fecha_presentacion: string | null
  fecha_pago: string | null
  presentado: boolean | null
  pagado: boolean | null
}

/**
 * Las cinco tarjetas de la planilla de vencimientos, contadas sobre las filas que le corresponden
 * al usuario según el interruptor "Míos / Todo el estudio" (ORR-382).
 *
 * El scoping se aplica ANTES de contar y reusa `filtrarVencimientosPorResponsable`, que es la
 * definición única de audiencia. Escribir acá una segunda regla es exactamente el bug de ORR-370.
 *
 * Vivía adentro de `calendarioService.obtenerEstadisticasVencimientos`, pegada al fetch. Se mudó
 * para poder testearla: son los números que el estudio mira todos los días y no tenían un solo test.
 */
export const calcularEstadisticasVencimientos = (
  filas: FilaEstadistica[],
  scoping?: ScopingResponsable,
): EstadisticasVencimientos => {
  const rows = scoping ? filtrarVencimientosPorResponsable(filas, scoping) : filas

  const hoy = startOfDay(new Date())
  const finSemana = addDays(hoy, 7)
  const inicioMes = startOfMonth(hoy)
  const finMes = endOfMonth(hoy)

  const esPendiente = (r: FilaEstadistica) =>
    r.estado === 'pendiente' && !r.presentado && !r.pagado

  const esPresentado = (r: FilaEstadistica) =>
    r.presentado === true || r.estado === 'presentado'

  const esPagado = (r: FilaEstadistica) => r.pagado === true || r.estado === 'pagado'

  /** Pendientes cuya fecha cae en la ventana pedida. `desde` en null = sin borde inferior. */
  const pendientesEntre = (desde: Date | null, hasta: Date | null) =>
    rows.filter((r) => {
      if (!esPendiente(r)) return false
      const f = startOfDay(parseISO(r.fecha_vencimiento))
      if (desde && isBefore(f, desde)) return false
      if (hasta && isAfter(f, hasta)) return false
      return true
    }).length

  /** Presentados o pagados cuyo día de cierre cae en el mes en curso. La fecha de cierre se lee como día calendario. */
  const cerradosEsteMes = (
    aplica: (r: FilaEstadistica) => boolean,
    fechaDe: (r: FilaEstadistica) => string | null,
  ) =>
    rows.filter((r) => {
      if (!aplica(r)) return false
      const f = startOfDay(parseISO(diaCalendario(fechaDe(r)) ?? r.fecha_vencimiento))
      return !isBefore(f, inicioMes) && !isAfter(f, hoy)
    }).length

  const vencidos = rows.filter((r) => {
    if (!esPendiente(r)) return false
    return isBefore(startOfDay(parseISO(r.fecha_vencimiento)), hoy)
  }).length

  const vencenEstaSemana = pendientesEntre(hoy, finSemana)
  const vencenEsteMes = pendientesEntre(hoy, finMes)
  const presentadosEsteMes = cerradosEsteMes(esPresentado, (r) => r.fecha_presentacion)
  const pagadosEsteMes = cerradosEsteMes(esPagado, (r) => r.fecha_pago)

  return {
    vencidos,
    vencenEstaSemana,
    vencenEsteMes,
    presentadosEsteMes,
    pagadosEsteMes,
    // Alias de compatibilidad
    proximaSemana: vencenEstaSemana,
    proximoMes: vencenEsteMes,
    pendientes: rows.filter(esPendiente).length,
    presentados: rows.filter(esPresentado).length,
    pagados: rows.filter(esPagado).length,
    enPlanPago: rows.filter((r) => r.estado === 'plan_pago').length,
  }
}

const ESTADOS_CERRADOS = new Set(['presentado', 'pagado', 'plan_pago'])

type FechasDelPeriodo = Pick<
  VencimientoConCliente,
  'estado' | 'estado_efectivo' | 'fecha_vencimiento' | 'fecha_presentacion' | 'fecha_pago'
>

/**
 * Días que ubican un vencimiento en el período de la Lista.
 * Un cerrado se ubica por su día de presentación y su día de pago, leídos como día calendario.
 * Si no tiene ninguno, se ubica por su vencimiento. Un pendiente o vencido se ubica por su vencimiento.
 */
const diasDelPeriodo = (v: FechasDelPeriodo): string[] => {
  const estado = v.estado_efectivo ?? v.estado ?? ''
  if (ESTADOS_CERRADOS.has(estado)) {
    const cierres = [diaCalendario(v.fecha_presentacion), diaCalendario(v.fecha_pago)].filter(
      (dia): dia is string => dia !== null,
    )
    if (cierres.length > 0) return cierres
  }
  return v.fecha_vencimiento ? [v.fecha_vencimiento] : []
}

/**
 * Dice si un vencimiento cae en el rango `desde`-`hasta` de la Lista. Los dos bordes son inclusivos.
 * Un borde vacío no limita, y un vencimiento sin fechas siempre entra.
 */
export const enPeriodoLista = (v: FechasDelPeriodo, desde?: string, hasta?: string): boolean => {
  const dias = diasDelPeriodo(v)
  if (dias.length === 0) return true
  const inicio = desde ? startOfDay(parseISO(desde)) : null
  const fin = hasta ? startOfDay(parseISO(hasta)) : null
  return dias.some((dia) => {
    const fecha = startOfDay(parseISO(dia))
    if (inicio && isBefore(fecha, inicio)) return false
    if (fin && isAfter(fecha, fin)) return false
    return true
  })
}

/**
 * Fuente de la Lista: todos los cerrados del período, más los próximos cuyo id no está entre ellos.
 * Con un mismo id en las dos fuentes queda la fila de los cerrados. Las filas repetidas de la vista
 * se conservan, igual que en la tarjeta.
 */
export const sumarCerradosAProximos = <T extends { id: string | null }>(
  proximos: T[],
  cerrados: T[],
): T[] => {
  const ids = new Set(cerrados.map((v) => v.id))
  return [...proximos.filter((v) => !ids.has(v.id)), ...cerrados]
}
